import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  CircularProgress,
  Tooltip,
  Paper,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
}));

const ViewRecordsDialog = ({
  open,
  onClose,
  records,
  loading,
  error,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  eventName,
}) => {
  const theme = useTheme();
  const [searchCompanyName, setSearchCompanyName] = useState('');
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

 const handleSearchByCompanyName = async () => {
  if (!searchCompanyName.trim()) return;

  try {
    setSearchLoading(true);
    setSearchError('');

    const res = await axios.get(`/api/v1/freedata/getallfreeDatarecordsflat`, {
      params: {
        search: searchCompanyName,
        searchBy: 'company_name', // Match backend logic
      },
    });

    setFilteredRecords(res.data.data || []);
  } catch (err) {
    setSearchError('Failed to fetch records by Company Name.');
    setFilteredRecords([]);
  } finally {
    setSearchLoading(false);
  }
};


  const renderCellWithTooltip = (content, maxLength = 20) => {
    if (!content) return '-';
    const displayText = content.length > maxLength
      ? `${content.substring(0, maxLength)}...`
      : content;

    return content.length > maxLength ? (
      <Tooltip title={content} arrow placement="top">
        <span>{displayText}</span>
      </Tooltip>
    ) : (
      displayText
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xxl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10],
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          fontWeight: 600,
        }}
      >
        Records for Event: {eventName || (records && records.length > 0 && records[0].event_name ? records[0].event_name : 'N/A')}
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 24px', gap: 16 }}>
          <input
            type="text"
            placeholder="Search by Company Name"
            value={searchCompanyName}
            onChange={(e) => setSearchCompanyName(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: `1px solid ${theme.palette.divider}`,
              flex: 1,
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearchByCompanyName}
            disabled={searchLoading}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {searchLoading ? 'Searching...' : 'Search'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setSearchCompanyName('');
              setFilteredRecords([]);
              setSearchError('');
            }}
            disabled={searchLoading}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Clear
          </Button>
        </div>

        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px'
          }}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="body1" sx={{ mt: 2 }}>Loading records...</Typography>
          </div>
        ) : error ? (
          <Paper elevation={0} sx={{
            padding: 3,
            margin: 2,
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.contrastText,
            borderRadius: 2
          }}>
            <Typography variant="h6">Error loading records</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>{error}</Typography>
          </Paper>
        ) : searchError ? (
          <Paper elevation={0} sx={{
            padding: 3,
            margin: 2,
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.contrastText,
            borderRadius: 2
          }}>
            <Typography variant="h6">Search Error</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>{searchError}</Typography>
          </Paper>
        ) : (filteredRecords.length === 0 && searchCompanyName) ? (
          <Paper elevation={0} sx={{
            padding: 3,
            margin: 2,
            backgroundColor: theme.palette.grey[100],
            borderRadius: 2,
            textAlign: 'center'
          }}>
            <Typography variant="body1">No records found for Company Name: {searchCompanyName}</Typography>
          </Paper>
        ) : (
          <Table
            size="small"
            aria-label="records table"
            sx={{
              minWidth: 1800,
              '& .MuiTableCell-root': {
                padding: '8px 16px',
                borderRight: `1px solid ${theme.palette.divider}`,
                '&:last-child': {
                  borderRight: 'none'
                }
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>id</StyledTableCell>
                <StyledTableCell>freedataSets_id</StyledTableCell>
                <StyledTableCell>Company Name</StyledTableCell>
                <StyledTableCell>Event Name</StyledTableCell>
                <StyledTableCell>Website</StyledTableCell>
                <StyledTableCell>Employee Count</StyledTableCell>
                <StyledTableCell>First Name</StyledTableCell>
                <StyledTableCell>Last Name</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>Verified</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(filteredRecords.length > 0 ? filteredRecords : records).map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>{renderCellWithTooltip(record.id)}</TableCell>
                  <TableCell>{renderCellWithTooltip(record.freedataSets_id)}</TableCell>
                  <TableCell>{renderCellWithTooltip(record.company_name)}</TableCell>
                  <TableCell>{renderCellWithTooltip(eventName)}</TableCell>
                  <TableCell>
                    {record.website ? (
                      <Tooltip title={record.website} arrow>
                        <a
                          href={record.website.startsWith('http') ? record.website : `https://${record.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: theme.palette.primary.main }}
                        >
                          Visit
                        </a>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{record.employee_count || '-'}</TableCell>
                  <TableCell>{renderCellWithTooltip(record.first_name)}</TableCell>
                  <TableCell>{renderCellWithTooltip(record.last_name)}</TableCell>
                  <TableCell>
                    {record.email ? (
                      <Tooltip title={`Email ${record.email}`} arrow>
                        <a
                          href={`mailto:${record.email}`}
                          style={{ color: theme.palette.primary.main }}
                        >
                          Email
                        </a>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <span style={{
                      color: record.verified ? theme.palette.success.main : theme.palette.error.main,
                      fontWeight: 500
                    }}>
                      {record.verified ? 'Yes' : 'No'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{
        justifyContent: 'space-between',
        padding: '16px 24px',
        borderTop: `1px solid ${theme.palette.divider}`
      }}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              marginBottom: 0
            }
          }}
        />
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 2,
            padding: '8px 24px',
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewRecordsDialog;
